// API client and base URL. Imports startSubscriptionRedirect lazily (it lives
// in modals/paywall.js and itself calls into this module for API_BASE — the two
// form a small mutual-dependency that ESM handles via live bindings as long as
// neither calls the other at module-load time).
import { startSubscriptionRedirect } from "../modals/paywall.js";

const IS_LOCALHOST =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const IS_SENDINVO_DOMAIN =
  window.location.hostname === "sendinvo.com" || window.location.hostname.endsWith(".sendinvo.com");

// Local dev serves UI on :8080 and API on :3000. Hosted deploys call the API service directly.
/** @type {string} Origin (no trailing slash) of the API. */
export const API_BASE = IS_LOCALHOST
  ? `${window.location.protocol}//${window.location.hostname}:3000`
  : IS_SENDINVO_DOMAIN
    ? "https://api.sendinvo.com"
    : "https://ar-ap-api-frankfurt.onrender.com";

/**
 * @typedef {object} ApiOptions
 * @property {string}                 [method]      HTTP method (default "GET", or "POST" if body present)
 * @property {BodyInit|object|null}   [body]        Object-as-body will be JSON-stringified by the caller; we set content-type only when it isn't already
 * @property {Record<string,string>}  [headers]     Extra headers; content-type defaults to application/json when body is present and not FormData
 * @property {AbortSignal}            [signal]
 */

/**
 * Wrapped fetch that prefixes API_BASE, always includes credentials, JSON-parses
 * responses, throws on non-2xx, and intercepts 402 SubscriptionRequired to send
 * the user straight to the Stripe billing flow.
 *
 * @param {string}     path            API path beginning with "/"
 * @param {ApiOptions} [options]
 * @returns {Promise<any>} parsed JSON body, or null for 204 No Content
 * @throws  {Error} when the response is not ok (message comes from body.message
 *                  / body.error / fallback "Request failed (status).")
 */
export async function api(path, options = {}) {
  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    ...(options.headers || {})
  };
  if (hasBody && !isFormData && !headers["content-type"] && !headers["Content-Type"]) {
    headers["content-type"] = "application/json";
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...options
  });

  if (res.status === 204) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 402 SubscriptionRequired → send the user straight to the Stripe billing
    // flow instead of surfacing the raw error. The send/create endpoints
    // return this shape when the company has no active Sendinvo subscription.
    if (res.status === 402 && body && body.error === "SubscriptionRequired") {
      try {
        startSubscriptionRedirect({ status: body.subscriptionStatus || "none" });
      } catch {
        // If the redirect fails to start for any reason, fall through to the error.
      }
    }
    throw new Error(body.message || body.error || `Request failed (${res.status}).`);
  }
  return body;
}
